import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useCart } from "../../context/CartContext";
import { formatCurrency, formatDiscount } from "../../utils/currency";
import StarRating from "../ui/StarRating";
import Badge from "../ui/Badge";

/* Cart+ SVG Icon */
const CartPlusIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 1h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 3M7 13l-1.4 5M17 18a1 1 0 100-2 1 1 0 000 2zM9 18a1 1 0 100-2 1 1 0 000 2z" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13 5v4M11 7h4" strokeLinecap="round"/>
  </svg>
);

const ProductCard = ({ product, view = "grid" }) => {
  const { addToCart, items } = useCart();

  const discount = formatDiscount(product.price, product.compareAtPrice);
  const inCart   = items.some(i => i.id === product.id);

  const handleAddToCart = (e) => {
    e.preventDefault();
    addToCart(product, 1);
    toast.success(`${product.title} added to cart!`);
  };

  /* ── LIST VIEW ── */
  if (view === "list") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="group card flex gap-5 p-5 hover:shadow-card-hover transition-all duration-300"
      >
        <Link to={`/product/${product.slug}`} className="flex-shrink-0">
          <div className="w-32 h-32 rounded-2xl overflow-hidden bg-surface-100">
            <img src={product.images[0]} alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          </div>
        </Link>
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
          <div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {product.badges?.map(b => <Badge key={b} label={b} />)}
            </div>
            <Link to={`/product/${product.slug}`}>
              <h3 className="font-display font-semibold text-gray-900 hover:text-brand-600 transition-colors text-base leading-snug line-clamp-1">
                {product.title}
              </h3>
            </Link>
            <p className="text-sm text-gray-400 font-sans mt-1 line-clamp-1">{product.shortDescription}</p>
            <div className="mt-2"><StarRating rating={product.rating} reviewCount={product.reviewCount} /></div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-baseline gap-2">
              <span className="font-display font-bold text-gray-900 text-lg">{formatCurrency(product.price)}</span>
              {product.compareAtPrice && (
                <span className="text-sm text-gray-400 line-through">{formatCurrency(product.compareAtPrice)}</span>
              )}
            </div>
            <button onClick={handleAddToCart} className="btn-primary text-sm py-2.5 px-5">
              <CartPlusIcon />
              {inCart ? "Add More" : "Add to Cart"}
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  /* ── GRID VIEW ── */
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25 }}
      className="group card hover:shadow-card-hover transition-all duration-300"
    >
      <Link to={`/product/${product.slug}`} className="block">
        {/* Image */}
        <div className="relative aspect-square bg-surface-100 overflow-hidden">
          <img
            src={product.images[0]}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          {/* Gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.badges?.map(b => <Badge key={b} label={b} />)}
          </div>

          {/* Discount pill */}
          {discount && (
            <div className="absolute top-3 right-3 bg-gradient-to-br from-rose-500 to-rose-700 text-white text-[11px] font-display font-bold px-2 py-1 rounded-xl shadow">
              -{discount}%
            </div>
          )}

          {/* Quick-add button — appears on hover */}
          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
            <button
              onClick={handleAddToCart}
              className="w-10 h-10 bg-brand-600 text-white rounded-xl flex items-center justify-center shadow-glow hover:bg-brand-700 hover:scale-110 active:scale-95 transition-all duration-200"
              aria-label="Add to cart"
            >
              <CartPlusIcon />
            </button>
          </div>
        </div>
      </Link>

      {/* Info */}
      <div className="p-4">
        <p className="text-[11px] font-display font-semibold text-brand-500 uppercase tracking-widest mb-1">{product.category}</p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-display font-semibold text-gray-900 hover:text-brand-600 transition-colors text-sm leading-snug line-clamp-2 mb-2">
            {product.title}
          </h3>
        </Link>
        <StarRating rating={product.rating} reviewCount={product.reviewCount} />
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-baseline gap-1.5">
            <span className="font-display font-bold text-gray-900">{formatCurrency(product.price)}</span>
            {product.compareAtPrice && (
              <span className="text-xs text-gray-400 line-through font-sans">{formatCurrency(product.compareAtPrice)}</span>
            )}
          </div>
          {/* Visible cart button on mobile (hover version hidden on mobile) */}
          <button
            onClick={handleAddToCart}
            className="md:hidden w-9 h-9 bg-brand-600 text-white rounded-xl flex items-center justify-center hover:bg-brand-700 transition-colors"
            aria-label="Add to cart"
          >
            <CartPlusIcon />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
